import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import FormTextareaField from '../../../components/ui/form/FormTextareaField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import RecordGroupField from '../../recordGroups/components/RecordGroupField'
import { meetingGroupFormSchema, type MeetingGroupFormValues } from './meetingGroupFormSchema'
import type { MeetingGroup, MeetingGroupUpsert } from '../types'

interface Props {
  visible: boolean
  initial: MeetingGroup | null
  submitting: boolean
  nameError?: string | null
  onClearNameError: () => void
  onSubmit: (payload: MeetingGroupUpsert) => void
  onHide: () => void
}

/** Toplantı grubu ekle/düzenle formu (038: react-hook-form + zod). Grup (122) ve ad zorunlu; açıklama opsiyonel. */
export default function MeetingGroupFormDialog({
  visible,
  initial,
  submitting,
  nameError,
  onClearNameError,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError, setValue } = useZodForm(meetingGroupFormSchema, {
    defaultValues: { groupId: null, name: '', description: '' },
  })

  useEffect(() => {
    if (visible)
      reset({
        groupId: initial?.groupId ?? null,
        name: initial?.name ?? '',
        description: initial?.description ?? '',
      })
  }, [visible, initial, reset])

  useEffect(() => {
    if (nameError) {
      setError('name', { type: 'server', message: nameError })
      onClearNameError()
    }
  }, [nameError, setError, onClearNameError])

  const preselectGroup = useCallback((id: number) => setValue('groupId', id), [setValue])

  const onValid = (v: MeetingGroupFormValues) =>
    onSubmit({
      groupId: v.groupId as number,
      name: v.name,
      description: v.description ? v.description : null,
    })

  return (
    <FormDialog
      visible={visible}
      header={initial ? t('meetings.form.editTitle') : t('meetings.form.newTitle')}
      submitLabel={t('meetings.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <RecordGroupField
          control={control}
          name="groupId"
          visible={visible}
          current={initial ? { id: initial.groupId, name: initial.groupName } : null}
          onPreselect={preselectGroup}
        />
        <FormTextField
          control={control}
          name="name"
          label={t('meetings.form.name')}
          required
          autoFocus
        />
        <FormTextareaField
          control={control}
          name="description"
          label={t('meetings.form.description')}
        />
      </div>
    </FormDialog>
  )
}
